import Search from '@ui/input/search';
import { type FC, memo, useState, type ReactElement, cloneElement, useEffect } from 'react';
import styles from './index.module.scss';
import { useCopyToClipboard, useToggle } from 'react-use';
import { bigNumberToReadable } from '@utils/big-number-to-readable';
import { formatAddress } from '@mysten/sui/utils';
import coinTypeCopyIcon from '@assets/images/parrot/copy.svg';
import { toast } from 'react-toastify';
import CoinLogo from '@ui/coin-logo';
import Nothing from '@components/nothing';
import { useParrotSearchCoin } from '@hooks/use-parrot-search-coin';
import type { ParrotCoinStruct } from 'src/types';
import { useParrotBalances } from '@hooks/use-parrot-balance';
import { normalizeSuiCoinType } from '@utils/index';
import { useModel } from 'foca';
import { coinParrotModel } from '@models/coin.parrot.model';
import Dialog from '@ui/parrot/dialog';
import { useAddress } from '@hooks/use-address';

interface OwnProps {
  filterCoins?: string | string[];
  disableCoins?: string | string[];
  onSelected: (value: ParrotCoinStruct) => void;
  children: ReactElement;
  title?: string;
  searchEnable?: boolean;
  disabled?: boolean;
  popularEnable?: boolean;
  quoteEnable?: boolean;
}

const SelectToken: FC<OwnProps> = (props) => {
  const {
    onSelected,
    children,
    searchEnable = true,
    disabled,
    popularEnable = true,
    quoteEnable = false,
  } = props;
  const address = useAddress();
  const [dialogVisible, toggleDialog] = useToggle(false);
  const disableCoins = props.disableCoins ? ([] as string[]).concat(props.disableCoins) : [];
  const filterCoins = props.filterCoins ? ([] as string[]).concat(props.filterCoins) : [];
  const [searchName, setSearchName] = useState('');
  const { balances } = useParrotBalances();

  const { coins } = useParrotSearchCoin(searchName);
  const { popularCoins, quoteCoins } = useModel(coinParrotModel);
  const [, copy] = useCopyToClipboard();

  useEffect(() => {
    if (!dialogVisible) {
      setSearchName('');
    }
  }, [dialogVisible]);

  const finalCoins = quoteEnable ? quoteCoins : coins;

  return (
    <>
      {cloneElement(children, {
        onClick: () => {
          if (disabled) {
            return;
          }
          toggleDialog();
        },
      })}
      <Dialog
        isShown={dialogVisible}
        width={400}
        title={props.title || 'Select a token or enter Coin Type'}
        onCloseComplete={toggleDialog}
      >
        {({ close }) => (
          <div className={styles.wrapper}>
            {searchEnable && (
              <Search
                value={searchName}
                wrapperClassName={styles.search}
                focusClassName={styles.search_focus}
                onChangeText={setSearchName}
                placeholder={'Search token'}
              />
            )}
            {popularEnable && (
              <div className={styles.popular}>
                {popularCoins.map((coin) => {
                  const disabled = disableCoins.some((item) => item === coin.type);
                  return (
                    <div
                      className={`${styles.item} ${disabled ? styles.disabled : ''}`}
                      key={coin.type}
                      onClick={() => {
                        if (disabled) return;
                        onSelected(coin);
                        close();
                      }}
                    >
                      <CoinLogo src={coin.iconUrl || ''} />
                      <div className={styles.right}>{coin.symbol}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {finalCoins.length < 1 && (
              <div className={styles.nothing}>
                <Nothing text={'No tokens found Or you can enter Coin Type'} />
              </div>
            )}
            {finalCoins && (
              <ul>
                {finalCoins.map((coin) => {
                  const disabled = disableCoins.some((item) => item === coin.type);
                  const filter = filterCoins.some((item) => item === coin.type);
                  if (filter) {
                    return null;
                  }
                  const clickEvent = disabled
                    ? undefined
                    : () => {
                        onSelected(coin);
                        close();
                      };
                  const balance =
                    balances.find(
                      (item) =>
                        normalizeSuiCoinType(item.coinType) === normalizeSuiCoinType(coin.type),
                    )?.totalBalance ?? '0';

                  return (
                    <li
                      key={coin.type}
                      className={disabled ? styles.disable : ''}
                      onClick={clickEvent}
                    >
                      <CoinLogo src={coin.iconUrl || ''} />
                      <div className={styles.content}>
                        <p className={styles.symbol}>{coin.name}</p>
                        <p>
                          {coin.symbol}
                          <span>{formatAddress(coin.type)} </span>
                          <img
                            src={coinTypeCopyIcon}
                            onClick={(event) => {
                              event.stopPropagation();
                              copy(coin.type);
                              toast('Address is copied');
                            }}
                          />
                        </p>
                      </div>
                      <div className={styles.balance}>
                        {address ? bigNumberToReadable(balance, coin.decimals) : ''}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
};

export default memo(SelectToken);
